// Frontend mirror of apps/lms/auto_grading/rubric_templates.py.
// Server is authoritative — keep these in sync when backend templates change.

export const ASSIGNMENT_RUBRIC_TEMPLATE = `# Assignment Fallback Rubric

This rubric is used by the AI grader ONLY when a task has no per-task
rubric. Prefer authoring rubrics on each task instead.

## Scoring Scale (per task, scaled to task_marks)
- Excellent  (90-100%): All criteria met with clear, specific evidence.
- Proficient (75-89%):  Most criteria met; minor gaps in 1 area.
- Developing (50-74%):  Partial — some criteria missed or weak.
- Insufficient (0-49%): Off-topic, missing, copied, or incorrect.

## General Criteria
1. Relevance to the prompt — does the response address what was asked?
2. Accuracy of content — is the information correct?
3. Depth and reasoning — does the student explain the "why"?
4. Use of evidence / examples from the learning material.

## Auto-Fail Conditions (score = 0)
- Submission is empty, off-topic, or copied verbatim from sources.
`;

export const TASK_RUBRIC_TEMPLATE = `# Task Rubric

## What this task tests
<<One sentence describing the skill/knowledge being assessed.>>

## Scoring Scale (out of <<task_marks>>)
- Excellent  (90-100%): All criteria met with clear, specific evidence.
- Proficient (75-89%):  Most criteria met; minor gaps in 1 area.
- Developing (50-74%):  Partial — some criteria missed or weak.
- Insufficient (0-49%): Off-topic, missing, copied, or incorrect.

## Criteria
1. <<Criterion 1 name>> — weight <<X>>%
   Look for: <<concrete observable evidence the AI can check>>
2. <<Criterion 2 name>> — weight <<X>>%
   Look for: <<concrete observable evidence>>
3. <<Criterion 3 name>> — weight <<X>>%
   Look for: <<concrete observable evidence>>

## Required Elements (must be present)
- <<E.g. "A specific example from the case study">>

## Auto-Fail Conditions (score = 0)
- <<E.g. "Submission is empty or off-topic">>
`;

export const TEXT_TASK_RUBRIC_EXAMPLE = `# Task Rubric — Short Answer

## What this task tests
The student's ability to explain a concept clearly and accurately
in their own words.

## Scoring Scale (out of <<task_marks>>)
- Excellent  (90-100%): Clear, accurate, complete explanation.
- Proficient (75-89%):  Mostly correct; one minor gap.
- Developing (50-74%):  Partial understanding; missing key points.
- Insufficient (0-49%): Incorrect, off-topic, or empty.

## Criteria
1. Accuracy of definition — weight 40%
   Look for: correct terminology and factual claims.
2. Completeness — weight 30%
   Look for: covers each required sub-point named in the prompt.
3. Clarity & own words — weight 30%
   Look for: clear sentences, not copy-pasted from source material.

## Required Elements (must be present)
- A definition of the concept in the student's own words.
- At least one example illustrating the concept.

## Auto-Fail Conditions (score = 0)
- Submission is empty, off-topic, or copy-pasted without attribution.
`;

export const CASE_STUDY_TASK_RUBRIC_EXAMPLE = `# Task Rubric — Case Study

## What this task tests
The student's ability to apply course concepts to a realistic
clinical / professional scenario.

## Scoring Scale (out of <<task_marks>>)
- Excellent  (90-100%): Identifies all key issues and proposes a
  sound, well-reasoned course of action.
- Proficient (75-89%):  Identifies most issues; reasoning is mostly
  sound with minor gaps.
- Developing (50-74%):  Misses an important issue or makes a
  significant reasoning error.
- Insufficient (0-49%): Misses the main issue entirely or proposes
  unsafe / incorrect action.

## Criteria
1. Issue identification — weight 30%
   Look for: names the central problem(s) in the scenario.
2. Application of course concepts — weight 35%
   Look for: cites specific concepts/procedures from the module.
3. Reasoned action plan — weight 25%
   Look for: a step-by-step plan with justification per step.
4. Awareness of scope / limits — weight 10%
   Look for: acknowledges when to escalate or seek help.

## Required Elements (must be present)
- A reference to the scenario described in the case study.
- At least one specific concept from the lesson.

## Auto-Fail Conditions (score = 0)
- Proposes an action that would cause harm.
- Submission is empty or unrelated to the case.
`;

export const FILE_UPLOAD_TASK_RUBRIC_EXAMPLE = `# Task Rubric — File Upload

## What this task tests
The student's ability to produce a structured artifact (document,
care plan, worksheet, etc.) that demonstrates the required skill.

## Scoring Scale (out of <<task_marks>>)
- Excellent  (90-100%): All required sections present, accurate, and
  well-organized.
- Proficient (75-89%):  All sections present; minor errors.
- Developing (50-74%):  One section missing or substantially weak.
- Insufficient (0-49%): Multiple sections missing or unreadable.

## Criteria
1. Required sections present — weight 40%
   Look for: each section named in the prompt is included.
2. Accuracy of content — weight 35%
   Look for: factual claims and procedures are correct.
3. Structure & readability — weight 25%
   Look for: headings, bullet points, or numbered lists where
   appropriate; consistent formatting.

## Required Elements (must be present)
- Student name or identifier at the top of the document.
- Each section header explicitly labelled.

## Auto-Fail Conditions (score = 0)
- File is unreadable, empty, or wrong file type.
- Submitted content is unrelated to the assigned task.
`;

export const RUBRIC_TEMPLATE_OPTIONS = [
  {
    value: 'assignment',
    label: 'Assignment fallback rubric',
    template: ASSIGNMENT_RUBRIC_TEMPLATE,
    scope: 'assignment',
  },
  { value: 'generic', label: 'Task rubric — generic', template: TASK_RUBRIC_TEMPLATE, scope: 'task' },
  { value: 'text', label: 'Example: short-answer', template: TEXT_TASK_RUBRIC_EXAMPLE, scope: 'task' },
  { value: 'case_study', label: 'Example: case study', template: CASE_STUDY_TASK_RUBRIC_EXAMPLE, scope: 'task' },
  { value: 'upload', label: 'Example: file upload', template: FILE_UPLOAD_TASK_RUBRIC_EXAMPLE, scope: 'task' },
];

// review_status display helpers
export const REVIEW_STATUS_META = {
  pending: { label: 'Pending', variant: 'secondary', icon: 'schedule' },
  in_review: { label: 'In Review', variant: 'warning', icon: 'rate_review' },
  graded: { label: 'Graded', variant: 'success', icon: 'check_circle' },
  ai_grading_in_progress: { label: 'AI Grading…', variant: 'info', icon: 'autorenew' },
  ai_graded: { label: 'AI Graded', variant: 'success', icon: 'auto_awesome' },
  ai_grading_failed: { label: 'AI Grading Failed', variant: 'danger', icon: 'error' },
};

export const GRADING_METHOD_META = {
  manual: { label: 'Manual', variant: 'secondary' },
  ai: { label: 'AI', variant: 'primary' },
  ai_human_reviewed: { label: 'AI + Human', variant: 'outline-primary' },
};
